import React, { useEffect, useRef, useState } from "react";
import "./Projects.css";

const personalProjects = [
  {
    name: "Binge2Gether",
    languages: "SwiftUI, Supabase, REST APIs",
    description:
      "A social movie platform designed to help users discover, rate, review, and discuss movies in a more interactive way. The app focuses on community engagement, personalized watch experiences, and a clean mobile-first experience.",
    linkLabel: "App Coming Soon",
    link: ""
  },
  {
    name: "Imposter Game",
    languages: "JavaScript, HTML, CSS",
    description:
      "A browser-based social deduction game built to create a fun and interactive multiplayer experience. The project emphasizes responsive gameplay, clean user interaction, and front-end logic for a smooth player experience.",
    linkLabel: "View Project",
    link: "https://github.com/BenBoog04/Imposter_Game_website"
  },
  {
    name: "Personal Portfolio",
    languages: "React, JavaScript, HTML, CSS",
    description:
      "A personal portfolio website built to showcase my background, technical skills, projects, and professional growth in a modern, recruiter-friendly format. The site highlights both my technical work and personal brand.",
    linkLabel: "Portfolio Home",
    link: "https://benbooganim.com/"
  },
  {
    name: "PreGame AI",
    languages: "React, JavaScript, Python, FastAPI, SQLAlchemy",
    description:
      "A full-stack event and social planning platform that allows users to create, discover, and manage events while coordinating pregames, attendees, and logistics. Features include role-based dashboards, organization management, invite systems, and AI-driven enhancements to streamline event planning and user engagement.",
    linkLabel: "PreGame AI",
    link: "https://pregameai.app/"
  },
  {
    name: "ApplyTrack",
    languages: "React, Python, FastAPI, MongoDB",
    description:
      "A full-stack job application tracker built to bring structure to the job search. Users can log applications with rich metadata — company, role, status, salary range, work type, and notes while auto-fetching company logos via the Clearbit API. Features a calendar view for tracking timelines, JWT-based authentication, and an integrated AI assistant for application advice and pipeline insights.",
    linkLabel: "ApplyTrack",
    link: "https://job-tracker-ten-phi.vercel.app/"
  }
];

const academicProjects = [
  {
    name: "GM3 Solutions: Skillforge Soft-Skills AI",
    languages: "Python, NLP, AI APIs, Full-Stack Tools",
    description:
      "An AI-powered interview coaching platform designed to analyze communication performance and provide feedback on soft skills. The system combines multimodal input, structured evaluation, and intelligent feedback to support professional development.",
    linkLabel: "Skillforge Soft-Skills AI",
    link: "https://forgedsoftskills-ai.vercel.app/"
  },
  {
    name: "Rate Your Plate",
    languages: "SwiftUI, Firebase, MapKit, CoreLocation",
    description:
      "A mobile application that allows users to rate meals, share dining experiences, and explore restaurants nearby. The project combines mobile design, cloud-backed storage, and location services into one user-friendly app.",
    linkLabel: "Technical Presentation",
    link: "https://www.youtube.com/watch?v=IpRXA5yu7Uk"
  },
  {
    name: "NFL Jersey Index",
    languages: "Python, SQL, Flask, PostgreSQL, HTML, CSS",
    description:
      "A data-driven project focused on organizing and analyzing large jersey sales datasets. The application demonstrates database design, indexing concepts, query performance, and sports-focused data exploration.",
    linkLabel: "View Project",
    link: "https://github.com/BenBoog04/NFL_Jersey_Index"
  },
  {
    name: "Fitness Calculator App",
    languages: "SwiftUI, JavaScript, HTML, CSS",
    description:
      "A fitness-focused application built to help users calculate health and body metrics through a clean and intuitive interface. The project emphasizes usability, responsive design, and practical everyday functionality.",
    linkLabel: "Demo via Request",
    link: ""
  },
  {
    name: "Automated Answering Agent",
    languages: "Python, AI APIs, NLP",
    description:
      "An automated answering system built to process questions and generate intelligent responses. The project explores AI integration, prompt design, and workflow automation for practical real-world use cases.",
    linkLabel: "View Project",
    link: "https://github.com/BenBoog04/Automated-Answering-Agent"
  }
];

function RevealBlock({ children, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      {
        threshold: 0.18
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} ${visible ? "projects-visible" : "projects-hidden"}`}
    >
      {children}
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <div className="project-card glass-panel">
      <h3 className="project-name">{project.name}</h3>

      <p className="project-languages">
        <span>Languages Used:</span> {project.languages}
      </p>

      <p className="project-description">{project.description}</p>

      {project.link ? (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="project-link"
        >
          Link: {project.linkLabel}
        </a>
      ) : (
        <p className="project-link static-link">Link: {project.linkLabel}</p>
      )}
    </div>
  );
}

export default function Projects() {
  return (
    <div className="projects-container">
      <RevealBlock className="projects-hero">
        <p className="projects-kicker">Projects</p>
        <h1 className="projects-title">My Work</h1>
        <p className="projects-subtitle">
          A collection of personal builds and academic projects that reflect my
          growth in software engineering, data systems, machine learning, and
          application development.
        </p>
      </RevealBlock>

      <section className="projects-section">
        <RevealBlock className="section-header">
          <h2 className="section-title">Personal</h2>
          <p className="section-subtitle">
            Independent projects that reflect my creativity, initiative, and
            development outside the classroom.
          </p>
        </RevealBlock>

        <div className="projects-grid">
          {personalProjects.map((project) => (
            <RevealBlock key={project.name}>
              <ProjectCard project={project} />
            </RevealBlock>
          ))}
        </div>
      </section>

      <section className="projects-section">
        <RevealBlock className="section-header">
          <h2 className="section-title">Academic</h2>
          <p className="section-subtitle">
            Coursework and capstone-based projects that highlight technical
            depth, collaboration, and structured problem solving.
          </p>
        </RevealBlock>

        <div className="projects-grid">
          {academicProjects.map((project) => (
            <RevealBlock key={project.name}>
              <ProjectCard project={project} />
            </RevealBlock>
          ))}
        </div>
      </section>
    </div>
  );
}